import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';

import { selectIsAccountMenuOpen } from '../../navigation/reducer';
import {
  sendEventToGA,
  GA_EVENT_HELP_GUIDE_CLICK,
  GA_EVENT_HELP_CONTACT_US_CLICK,
  GA_EVENT_ADMIN_TOOL_ICON_CLICK
} from '../../analytics/constants';

import { GamedayLogo, Link, ShoppingCartIcon, Icon } from '../../components/';
import { AccountDropdown } from '../accountDropdown/';
import { NavList } from '../navList/';
import AdminHeader from '../AdminHeader';

import { navLinksTypes } from '../../types/nav';
import { selectedDepartmentTypes } from '../../types/shop';
import { userAccountTypes } from '../../types/user';
import { TranslatedCopy } from '../../intl/TranslatedCopy';
import { getCanShowInvoicesLink } from '../../gamedayAccount/selectors';

import { canAccessSystemManagement } from '../../util/rolePermissions';

const navMainStyle = 'navigation__main_wrapper flex-container align-center space-between bg-primary-grey';
const navMainPadding = 'pt7-sm pb7-sm pl4-sm p0-lg';

const headerStyle = 'headline-5 text-uppercase u-bold d-sm-b';

const hideUntilDesktop = 'd-sm-h d-lg-ib';
const hideOnDesktop = 'd-lg-h d-sm-b';

const handleHelpGuideEvent = () => {
  sendEventToGA(GA_EVENT_HELP_GUIDE_CLICK);
};

const handleContactUsEvent = () => {
  sendEventToGA(GA_EVENT_HELP_CONTACT_US_CLICK);
};

const handleAdminToolEvent = () => {
  sendEventToGA(GA_EVENT_ADMIN_TOOL_ICON_CLICK);
};

/**
 * @name getCatalogsToolUrl
 * @desc Gets the url to return back Gameday based on current environment
 */
const getCatalogsToolUrl = () => {
  let env = '';
  const { hostname } = window.location;

  if (hostname.includes('localhost') || hostname.includes('dev')) {
    env = '-dev';
  } else if (hostname.includes('stg')) {
    env = '-stg';
  } else if (hostname.includes('qa')) {
    env = '-qa';
  }

  return `https://gameday-admin${env}.nike.net`;
};

export const NavigationMain = ({
  alwaysShowCart,
  isOpen,
  isShopping,
  logout,
  navLinks,
  selectedDepartment,
  toggleIsAdminCart,
  toggleNavMenu,
  user,
  userAccount,
  isAccountMenuOpen,
  canShowInvoicesLink
}) => {
  return (
    <div>
      <div className={classnames(navMainStyle, navMainPadding)}>
        <div className='navigation__main_settings ml0-sm ml5-lg mb5-sm mb0-lg mt1-sm'>
          {canAccessSystemManagement(user.role) && (
            <a href={getCatalogsToolUrl()} data-desc='settings' onClick={handleAdminToolEvent}>
              <Icon name='settings' alt='settings' />
            </a>
          )}
        </div>
        <div className='navigation__account flex-container row space-between mr3-sm mr5-lg'>
          <div className='d-sm-ib'>
            <div className={classnames(headerStyle, hideOnDesktop)}>Nike Gameday</div>
            <div className='body-4' data-desc='navigationMain__userName'>
              <span className='u-bold'>
                {user.firstName} {user.lastName}
              </span>
              <span>{userAccount && userAccount.name && ` | ${userAccount.number} ${userAccount.name}`}</span>
            </div>
            <div className={classnames(hideOnDesktop, 'body-4 u-bold')} data-desc='navigationMain__userLogin'>
              {user.login}
            </div>
          </div>
          <div className='d-sm-ib va-sm-t'>{user && <AccountDropdown userData={user} logout={logout} />}</div>
          <div className='navigation__helpLink pl4-md d-sm-h d-lg-b'>
            <div className='navigation__helpTrigger body-4 u-bold pt4-lg pb4-lg'>
              <TranslatedCopy id='route.user.section' />
            </div>
            <ul className='navigation__helpMenu z1 pb4-md pt4-md prl6-md'>
              <li>
                <div onClick={handleHelpGuideEvent}>
                  <Link
                    to={`${import.meta.env.VITE_HELP_GUIDE_URL}`}
                    extraClassName='body-3 u-bold d-sm-h d-lg-b text-color-secondary mb1-sm'
                    data-desc='help__link'
                    external
                  >
                    <TranslatedCopy id='nav.help.user' />
                  </Link>
                </div>
              </li>
              <li>
                <div onClick={handleContactUsEvent}>
                  <Link
                    to='/contact-us'
                    extraClassName='body-3 u-bold d-sm-h d-lg-b text-color-secondary mb1-sm'
                    data-desc='contactUs__link'
                  >
                    <TranslatedCopy id='global.contact' />
                  </Link>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div
        className={classnames('navigation__inner flex-container row space-between', {
          navigation__inner_accountOpen: isAccountMenuOpen
        })}
      >
        <NavList
          canAccessInvoices={canShowInvoicesLink}
          routes={navLinks}
          isOpen={isOpen}
          toggleNavMenu={toggleNavMenu}
        />
        <div className={classnames(hideUntilDesktop, 'logo flex-container align-center prl6-sm pt5-lg')}>
          <Link to='/' data-desc='navigationMain__link_home'>
            <GamedayLogo />
          </Link>
        </div>

        <div className={classnames(hideUntilDesktop, 'd-sm-ib mb4-md cart-container')}>
          {selectedDepartment && (alwaysShowCart || isShopping) && <ShoppingCartIcon />}
        </div>
      </div>
      <AdminHeader
        selectedDepartment={selectedDepartment}
        toggleIsAdminCart={toggleIsAdminCart}
        isShopping={isShopping}
        alwaysShowCart={alwaysShowCart}
      />
    </div>
  );
};

NavigationMain.defaultProps = {
  alwaysShowCart: false,
  isOpen: false,
  selectedDepartment: null,
  userAccount: null
};

NavigationMain.propTypes = {
  alwaysShowCart: PropTypes.bool,
  isOpen: PropTypes.bool,
  isShopping: PropTypes.bool.isRequired,
  logout: PropTypes.func.isRequired,
  navLinks: navLinksTypes.isRequired,
  selectedDepartment: selectedDepartmentTypes,
  toggleIsAdminCart: PropTypes.func.isRequired,
  toggleNavMenu: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
  userAccount: userAccountTypes,
  isAccountMenuOpen: PropTypes.bool.isRequired,
  canShowInvoicesLink: PropTypes.bool.isRequired
};

const mapStateToProps = state => ({
  isAccountMenuOpen: selectIsAccountMenuOpen(state),
  canShowInvoicesLink: getCanShowInvoicesLink(state)
});

export default connect(mapStateToProps, {})(NavigationMain);
